import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Switch,
  Alert,
  StatusBar,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';

export default function MoreScreen({ route }) {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Always authenticated in learning platform
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [emailUpdatesEnabled, setEmailUpdatesEnabled] = useState(true);
  const [learningProgress, setLearningProgress] = useState({
    totalCourses: 12,
    completedCourses: 5,
    certificates: 5,
    learningTime: 48,
    currentStreak: 7,
    overallProgress: 65,
  });

  // Load user data when screen focuses
  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
      loadSettings();
      loadLearningProgress();
    }, [])
  );

  const loadUserData = async () => {
    try {
      // Check route params first (passed from LearningDashboard)
      if (route?.params?.userData) {
        console.log('MoreScreen - Using route params user data:', route.params.userData);
        setUserData(route.params.userData);
        setIsAuthenticated(true);
        return;
      }

      // Check AsyncStorage for user data
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      console.log('MoreScreen - Loading user data from storage...');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.log('MoreScreen - Found stored user:', parsedUser);
        setUserData(parsedUser);
        setIsAuthenticated(true);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        console.log('MoreScreen - Found stored userData:', parsedUserData);
        setUserData(parsedUserData);
        setIsAuthenticated(true);
      } else {
        // Fallback user data for learning platform
        console.log('MoreScreen - Using fallback user data');
        setUserData({
          full_name: 'Learning User',
          email: 'user@jobeng.ai',
          user_type: 'learning',
          education_level: 'Bachelor\'s Degree',
        });
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Still set authenticated as true for learning platform
      setIsAuthenticated(true);
    }
  };

  const loadSettings = async () => {
    try {
      const notifications = await AsyncStorage.getItem('notifications_enabled');
      const darkMode = await AsyncStorage.getItem('dark_mode_enabled');
      const emailUpdates = await AsyncStorage.getItem('email_updates_enabled');
      
      if (notifications !== null) setNotificationsEnabled(JSON.parse(notifications));
      if (darkMode !== null) setDarkModeEnabled(JSON.parse(darkMode));
      if (emailUpdates !== null) setEmailUpdatesEnabled(JSON.parse(emailUpdates));
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadLearningProgress = async () => {
    try {
      const progress = await AsyncStorage.getItem('learning_progress');
      if (progress) {
        setLearningProgress(JSON.parse(progress));
      }
    } catch (error) {
      console.error('Error loading learning progress:', error);
    }
  };

  const saveSetting = async (key, value) => {
    try {
      await AsyncStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving setting:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Learning User';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Learning User';
  };

  const getUserEmail = () => {
    if (!userData) return 'user@jobeng.ai';
    return userData.email || userData.student_email || 'user@jobeng.ai';
  };

  const getUserEducation = () => {
    if (!userData) return 'Engineering Student';
    return userData.education_level || userData.educationLevel || 'Engineering Student';
  };

  const handleBackToDashboard = () => {
    navigation.navigate('LearningDashboard', { userData });
  };

  const handleLogout = () => {
    Alert.alert(
      'Sign Out',
      `Are you sure you want to sign out, ${getUserName()}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Sign Out',
          style: 'destructive',
          onPress: async () => {
            try {
              await AsyncStorage.multiRemove(['user', 'userData', 'authToken', 'learning_progress']);
              navigation.reset({
                index: 0,
                routes: [{ name: 'Home' }],
              });
            } catch (error) {
              console.error('Error during logout:', error);
            }
          },
        },
      ]
    );
  };

  // Mixed color scheme for different sections
  const sectionColors = {
    account: '#f59e0b', // Orange
    learning: '#10b981', // Green
    tools: '#06b6d4', // Cyan
    support: '#3b82f6', // Blue
    settings: '#8b5cf6', // Purple
  };

  const menuItems = [
    {
      title: 'Account & Profile',
      color: sectionColors.account,
      items: [
        {
          id: 'profile',
          label: 'My Profile',
          icon: 'account',
          screen: 'LearningProfileScreen',
          color: '#f59e0b',
          description: 'View and edit your profile information',
        },
        {
          id: 'edit-profile',
          label: 'Edit Profile',
          icon: 'account-edit',
          screen: 'EditLearningProfileScreen',
          color: '#fdb913',
          description: 'Update your personal information',
        },
        {
          id: 'certificates',
          label: 'My Certificates',
          icon: 'certificate',
          screen: 'LearningCertificateScreen',
          color: '#f97316',
          description: 'View and download earned certificates',
        },
        {
          id: 'progress',
          label: 'Learning History',
          icon: 'chart-line',
          screen: 'MyLearningHistoryScreen',
          color: '#ea580c',
          description: 'Track your learning progress',
        },
      ],
    },
    {
      title: 'Learning & Courses',
      color: sectionColors.learning,
      items: [
        {
          id: 'dashboard',
          label: 'Learning Dashboard',
          icon: 'view-dashboard',
          action: handleBackToDashboard,
          color: '#10b981',
          description: 'Return to your learning dashboard',
        },
        {
          id: 'courses',
          label: 'Browse Courses',
          icon: 'school',
          screen: 'LearningCourses',
          color: '#059669',
          description: 'Explore available engineering courses',
        },
        {
          id: 'books',
          label: 'Engineering Books',
          icon: 'book-open-variant',
          screen: 'EngineeringBooksScreen',
          color: '#047857',
          description: 'Access digital engineering textbooks',
        },
        {
          id: 'internships',
          label: 'Virtual Internships',
          icon: 'briefcase',
          screen: 'VirtualInternshipScreen',
          color: '#065f46',
          description: 'Apply for virtual work experience',
        },
        {
          id: 'skills',
          label: 'Skills Upgrade',
          icon: 'trending-up',
          screen: 'SkillsUpgradeCourses',
          color: '#064e3b',
          description: 'Enhance your technical skills',
        },
      ],
    },
    {
      title: 'Tools & Resources',
      color: sectionColors.tools,
      items: [
        {
          id: 'search',
          label: 'Search Learning Content',
          icon: 'magnify',
          screen: 'Search',
          color: '#06b6d4',
          description: 'Find courses, books, and resources',
        },
        {
          id: 'assignments',
          label: 'Assignments',
          icon: 'file-document-edit',
          screen: 'AssignmentScreen',
          color: '#0891b2',
          description: 'View and submit assignments',
        },
        {
          id: 'quizzes',
          label: 'Quizzes & Tests',
          icon: 'help-circle',
          screen: 'QuizScreen',
          color: '#0e7490',
          description: 'Take assessments and quizzes',
        },
        {
          id: 'downloads',
          label: 'My Downloads',
          icon: 'download',
          color: '#155e75',
          description: 'Manage downloaded content',
          action: () => Alert.alert('Downloads', 'Download management coming soon!'),
        },
      ],
    },
    {
      title: 'Support & Help',
      color: sectionColors.support,
      items: [
        {
          id: 'help',
          label: 'Help Center',
          icon: 'help-circle',
          color: '#3b82f6',
          description: 'Get help and support',
          action: () => Alert.alert('Help Center', 'Comprehensive help documentation coming soon!'),
        },
        {
          id: 'contact',
          label: 'Contact Support',
          icon: 'email',
          color: '#2563eb',
          description: 'Get in touch with our support team',
          action: () => Alert.alert('Contact Support', 'Email: support@jobeng.ai\\nPhone: +44 20 1234 5678'),
        },
        {
          id: 'feedback',
          label: 'Send Feedback',
          icon: 'message-text',
          color: '#1d4ed8',
          description: 'Share your thoughts and suggestions',
          action: () => Alert.alert('Feedback', 'Thank you for helping us improve JOBENG!'),
        },
        {
          id: 'about',
          label: 'About JOBENG',
          icon: 'information',
          color: '#1e40af',
          description: 'Learn more about our platform',
          action: () => Alert.alert('About JOBENG', 'JOBENG Learning Platform v1.0.0\\n\\nEmpowering UK engineering students with world-class education and career opportunities.'),
        },
      ],
    },
  ];

  const handleMenuItemPress = (item) => {
    if (item.screen) {
      navigation.navigate(item.screen, { userData });
    } else if (item.action) {
      item.action();
    }
  };

  const renderUserSection = () => {
    return (
      <TouchableOpacity
        style={styles.userSection}
        onPress={() => navigation.navigate('LearningProfileScreen', { userData })}
      >
        <View style={[styles.userAvatar, { backgroundColor: sectionColors.account }]}>
          <Text style={styles.userAvatarText}>
            {getUserName().charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.userInfo}>
          <Text style={styles.userName}>{getUserName()}</Text>
          <Text style={styles.userEmail}>{getUserEmail()}</Text>
          <Text style={styles.userEducation}>{getUserEducation()}</Text>
          <View style={[styles.subscriptionBadge, { backgroundColor: sectionColors.account }]}>
            <MaterialCommunityIcons name="school" size={12} color="#ffffff" />
            <Text style={styles.subscriptionText}>Learning Platform</Text>
          </View>
        </View>
        <MaterialCommunityIcons name="chevron-right" size={24} color="#9ca3af" />
      </TouchableOpacity>
    );
  };

  const renderStatsSection = () => {
    return (
      <View style={styles.statsSection}>
        <Text style={styles.statsTitle}>Your Learning Progress</Text>
        <View style={styles.statsGrid}>
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => navigation.navigate('LearningCoursesScreen', { userData })}
          >
            <View style={[styles.statIcon, { backgroundColor: '#10b981' }]}>
              <MaterialCommunityIcons name="book-open-variant" size={20} color="#ffffff" />
            </View>
            <Text style={styles.statValue}>{learningProgress.totalCourses}</Text>
            <Text style={styles.statLabel}>Courses</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => navigation.navigate('LearningCertificateScreen', { userData })}
          >
            <View style={[styles.statIcon, { backgroundColor: '#f59e0b' }]}>
              <MaterialCommunityIcons name="certificate" size={20} color="#ffffff" />
            </View>
            <Text style={styles.statValue}>{learningProgress.certificates}</Text>
            <Text style={styles.statLabel}>Certificates</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => navigation.navigate('MyLearningHistoryScreen', { userData })}
          >
            <View style={[styles.statIcon, { backgroundColor: '#3b82f6' }]}>
              <MaterialCommunityIcons name="clock" size={20} color="#ffffff" />
            </View>
            <Text style={styles.statValue}>{learningProgress.learningTime}h</Text>
            <Text style={styles.statLabel}>Learning Time</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.statItem}
            onPress={() => navigation.navigate('LearningProfileScreen', { userData })}
          >
            <View style={[styles.statIcon, { backgroundColor: '#8b5cf6' }]}>
              <MaterialCommunityIcons name="fire" size={20} color="#ffffff" />
            </View>
            <Text style={styles.statValue}>{learningProgress.currentStreak}</Text>
            <Text style={styles.statLabel}>Day Streak</Text>
          </TouchableOpacity>
        </View>
        
        {/* Overall Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressLabel}>Overall Progress</Text>
            <Text style={styles.progressPercentage}>{learningProgress.overallProgress}%</Text>
          </View>
          <View style={styles.progressBar}>
            <View 
              style={[
                styles.progressFill, 
                { 
                  width: `${learningProgress.overallProgress}%`,
                  backgroundColor: sectionColors.learning 
                }
              ]} 
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#f9fafb" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleBackToDashboard}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#6b7280" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>More</Text>
        <TouchableOpacity style={styles.headerButton}>
          <MaterialCommunityIcons name="cog" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* User Section */}
        {renderUserSection()}

        {/* Stats Section */}
        {renderStatsSection()}

        {/* Menu Sections */}
        {menuItems.map((section, sectionIndex) => (
          <View key={sectionIndex} style={styles.menuSection}>
            <View style={[styles.sectionHeader, { backgroundColor: section.color }]}>
              <Text style={styles.sectionTitle}>{section.title}</Text>
            </View>
            {section.items.map((item, itemIndex) => (
              <TouchableOpacity
                key={item.id}
                style={[
                  styles.menuItem,
                  itemIndex === section.items.length - 1 && styles.lastMenuItem,
                ]}
                onPress={() => handleMenuItemPress(item)}
              >
                <View style={styles.menuItemLeft}>
                  <View style={[styles.menuItemIcon, { backgroundColor: item.color }]}>
                    <MaterialCommunityIcons name={item.icon} size={20} color="#ffffff" />
                  </View>
                  <View style={styles.menuItemContent}>
                    <Text style={styles.menuItemText}>{item.label}</Text>
                    <Text style={styles.menuItemDescription}>{item.description}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#9ca3af" />
              </TouchableOpacity>
            ))}
          </View>
        ))}

        {/* Settings Section */}
        <View style={styles.menuSection}>
          <View style={[styles.sectionHeader, { backgroundColor: sectionColors.settings }]}>
            <Text style={styles.sectionTitle}>Settings & Preferences</Text>
          </View>
          
          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <View style={[styles.menuItemIcon, { backgroundColor: '#8b5cf6' }]}>
                <MaterialCommunityIcons name="bell" size={20} color="#ffffff" />
              </View>
              <View style={styles.menuItemContent}>
                <Text style={styles.menuItemText}>Push Notifications</Text>
                <Text style={styles.menuItemDescription}>Receive learning reminders and updates</Text>
              </View>
            </View>
            <Switch
              value={notificationsEnabled}
              onValueChange={(value) => {
                setNotificationsEnabled(value);
                saveSetting('notifications_enabled', value);
              }}
              trackColor={{ false: '#d1d5db', true: '#8b5cf6' }}
              thumbColor={notificationsEnabled ? '#ffffff' : '#f3f4f6'}
            />
          </View>

          <View style={styles.menuItem}>
            <View style={styles.menuItemLeft}>
              <View style={[styles.menuItemIcon, { backgroundColor: '#7c3aed' }]}>
                <MaterialCommunityIcons name="email" size={20} color="#ffffff" />
              </View>
              <View style={styles.menuItemContent}>
                <Text style={styles.menuItemText}>Email Updates</Text>
                <Text style={styles.menuItemDescription}>Get course updates and news via email</Text>
              </View>
            </View>
            <Switch
              value={emailUpdatesEnabled}
              onValueChange={(value) => {
                setEmailUpdatesEnabled(value);
                saveSetting('email_updates_enabled', value);
              }}
              trackColor={{ false: '#d1d5db', true: '#7c3aed' }}
              thumbColor={emailUpdatesEnabled ? '#ffffff' : '#f3f4f6'}
            />
          </View>

          <View style={[styles.menuItem, styles.lastMenuItem]}>
            <View style={styles.menuItemLeft}>
              <View style={[styles.menuItemIcon, { backgroundColor: '#6d28d9' }]}>
                <MaterialCommunityIcons name="theme-light-dark" size={20} color="#ffffff" />
              </View>
              <View style={styles.menuItemContent}>
                <Text style={styles.menuItemText}>Dark Mode</Text>
                <Text style={styles.menuItemDescription}>Switch to dark theme</Text>
              </View>
            </View>
            <Switch
              value={darkModeEnabled}
              onValueChange={(value) => {
                setDarkModeEnabled(value);
                saveSetting('dark_mode_enabled', value);
              }}
              trackColor={{ false: '#d1d5db', true: '#6d28d9' }}
              thumbColor={darkModeEnabled ? '#ffffff' : '#f3f4f6'}
            />
          </View>
        </View>

        {/* Logout Section */}
        <View style={styles.menuSection}>
          <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
            <View style={[styles.menuItemIcon, { backgroundColor: '#ef4444' }]}>
              <MaterialCommunityIcons name="logout" size={20} color="#ffffff" />
            </View>
            <View style={styles.logoutContent}>
              <Text style={styles.logoutButtonText}>Sign Out</Text>
              <Text style={styles.logoutDescription}>Sign out of your JOBENG account</Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* App Info */}
        <View style={styles.appInfo}>
          <View style={styles.appLogoContainer}>
            <View style={[styles.appLogo, { backgroundColor: sectionColors.account }]}>
              <MaterialCommunityIcons name="school" size={32} color="#ffffff" />
            </View>
            <Text style={styles.appName}>JOBENG Learning Platform</Text>
          </View>
          <Text style={styles.appVersion}>Version 1.0.0</Text>
          <Text style={styles.appCopyright}>© 2024 JOBENG. All rights reserved.</Text>
          <Text style={styles.appDescription}>
            Empowering UK engineering students with world-class education and career opportunities.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  headerButton: {
    padding: 5,
  },
  scrollView: {
    flex: 1,
  },
  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#fff',
    marginBottom: 15,
  },
  userAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  userAvatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 2,
  },
  userEducation: {
    fontSize: 12,
    color: '#9ca3af',
    marginBottom: 8,
  },
  subscriptionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  subscriptionText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  statsSection: {
    backgroundColor: '#fff',
    padding: 20,
    marginBottom: 15,
  },
  statsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  progressContainer: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  progressPercentage: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#10b981',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  menuSection: {
    backgroundColor: '#fff',
    marginBottom: 15,
    borderRadius: 12,
    overflow: 'hidden',
  },
  sectionHeader: {
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  lastMenuItem: {
    borderBottomWidth: 0,
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  menuItemIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  menuItemContent: {
    flex: 1,
  },
  menuItemText: {
    fontSize: 16,
    color: '#111827',
    fontWeight: '500',
    marginBottom: 2,
  },
  menuItemDescription: {
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 16,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  logoutContent: {
    marginLeft: 12,
  },
  logoutButtonText: {
    fontSize: 16,
    color: '#ef4444',
    fontWeight: '500',
    marginBottom: 2,
  },
  logoutDescription: {
    fontSize: 12,
    color: '#9ca3af',
  },
  appInfo: {
    alignItems: 'center',
    padding: 30,
    backgroundColor: '#fff',
    marginBottom: 20,
  },
  appLogoContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  appLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  appName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  appVersion: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 4,
  },
  appCopyright: {
    fontSize: 12,
    color: '#9ca3af',
    marginBottom: 8,
  },
  appDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
});
